import { useState, useEffect } from "react";
import { saDate, publicName } from "../lib/format";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { 
  MapPin, Calendar, Banknote, Users, Clock, 
  Briefcase, Filter, ArrowRight, Plus
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Input } from "../components/ui/input";
import SearchBar from "../components/SearchBar";
import BannerDisplay from "../components/BannerDisplay";
import SEO from "../components/SEO";
import axios from "axios";
import { API, useAuth } from "../App";

export default function JobBoard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [jobs, setJobs] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalJobs, setTotalJobs] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);

  const [filters, setFilters] = useState({
    query: searchParams.get("query") || "",
    skills: searchParams.get("skills") || "",
    location: searchParams.get("location") || "",
    budget_min: searchParams.get("budget_min") || "",
    budget_max: searchParams.get("budget_max") || "",
  });

  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    fetchJobs();
  }, [filters, currentPage]);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories`);
      setCategories(response.data);
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  };

  const fetchJobs = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.query) params.set("query", filters.query);
      if (filters.skills) params.set("skills", filters.skills);
      if (filters.location) params.set("location", filters.location);
      if (filters.budget_min) params.set("budget_min", filters.budget_min);
      if (filters.budget_max) params.set("budget_max", filters.budget_max);
      params.set("page", currentPage.toString());
      params.set("limit", "20");

      const response = await axios.get(`${API}/jobs?${params.toString()}`);
      setJobs(response.data.jobs);
      setTotalJobs(response.data.total);
    } catch (error) {
      console.error("Error fetching jobs:", error);
    } finally {
      setLoading(false);
    }
  };

  const updateFilter = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setCurrentPage(1);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return "Today";
    if (diffDays === 1) return "Yesterday";
    if (diffDays < 7) return `${diffDays} days ago`;
    return date.toLocaleDateString();
  };

  return (
    <div className="min-h-screen bg-background" data-testid="job-board-page">
      <SEO 
        url="/jobs"
        title="Creative Jobs in Cape Town"
        description="Find videography, film production, and creative jobs in Cape Town. Browse job listings and apply to work with top brands and agencies."
        keywords={["creative jobs cape town", "videography jobs", "film production jobs", "freelance work"]}
      />
      
      {/* Header */}
      <div className="bg-zinc-950 border-b border-zinc-800 py-8 px-4 md:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
            <div>
              <h1 className="font-heading text-title uppercase">
                JOB <span className="text-orange-500">BOARD</span>
              </h1>
              <p className="text-zinc-500 mt-1">
                Find your next creative project in Cape Town
              </p>
            </div>
            <Button
              onClick={() => navigate("/post-job")}
              className="bg-orange-600 hover:bg-orange-500"
              data-testid="post-job-btn"
            >
              <Plus className="h-4 w-4 mr-2" />
              POST A JOB
            </Button>
          </div>
          
          <div className="max-w-2xl">
            <SearchBar
              placeholder="Search jobs by title or description..."
              onSearch={(query) => updateFilter("query", query)}
            />
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 md:px-8 py-8">
        <BannerDisplay placement="jobs" className="mb-8" />
        {/* Filters */}
        <div className="flex flex-wrap items-center gap-4 mb-8">
          <Select
            value={filters.skills || "all"}
            onValueChange={(value) => updateFilter("skills", value === "all" ? "" : value)}
          >
            <SelectTrigger className="w-48 bg-zinc-900 border-zinc-700 text-zinc-300">
              <SelectValue placeholder="All Categories" />
            </SelectTrigger>
            <SelectContent className="bg-zinc-900 border-zinc-700">
              <SelectItem value="all">All Categories</SelectItem>
              {categories.map((cat) => (
                <SelectItem key={cat.category_id} value={cat.name}>
                  {cat.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Input
            placeholder="Location"
            value={filters.location}
            onChange={(e) => updateFilter("location", e.target.value)}
            className="w-40 bg-zinc-900 border-zinc-700 text-zinc-300"
          />

          <div className="flex items-center gap-2">
            <Input
              type="number"
              placeholder="Min Budget"
              value={filters.budget_min}
              onChange={(e) => updateFilter("budget_min", e.target.value)}
              className="w-32 bg-zinc-900 border-zinc-700 text-zinc-300"
            />
            <span className="text-zinc-600">-</span>
            <Input
              type="number"
              placeholder="Max Budget"
              value={filters.budget_max}
              onChange={(e) => updateFilter("budget_max", e.target.value)}
              className="w-32 bg-zinc-900 border-zinc-700 text-zinc-300"
            />
          </div>

          <p className="ml-auto text-zinc-500 text-sm">
            <span className="text-white font-medium">{totalJobs}</span> jobs found
          </p>
        </div>

        {/* Jobs List */}
        {loading ? (
          <div className="space-y-4">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-40 bg-zinc-900 skeleton" />
            ))}
          </div>
        ) : jobs.length > 0 ? (
          <div className="space-y-4">
            {jobs.map((job) => (
              <Link
                key={job.job_id}
                to={`/jobs/${job.slug || job.job_id}`}
                className="block bg-zinc-900 border border-zinc-800 hover:border-zinc-700 p-6 transition-all group"
                data-testid={`job-card-${job.job_id}`}
              >
                <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-start gap-4">
                      {/* Client Picture */}
                      <div className="hidden md:block w-12 h-12 rounded-full bg-zinc-800 overflow-hidden shrink-0">
                        {job.client_picture ? (
                          <img
                            src={job.client_picture}
                            alt={job.client_name}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-zinc-500 font-heading">
                            {job.client_name?.charAt(0)?.toUpperCase()}
                          </div>
                        )}
                      </div>

                      <div>
                        <h3 className="font-heading text-xl text-white group-hover:text-orange-500 transition-colors">
                          {job.title}
                        </h3>
                        <p className="text-zinc-500 text-sm mt-1">
                          Posted by {publicName(job.client_name)} • {saDate(job.created_at)}
                        </p>
                        
                        <p className="text-zinc-400 mt-3 line-clamp-2">
                          {job.description}
                        </p>

                        {/* Skills */}
                        <div className="flex flex-wrap gap-2 mt-3">
                          {job.skills_required?.slice(0, 4).map((skill) => (
                            <Badge
                              key={skill}
                              variant="outline"
                              className="bg-transparent border-zinc-700 text-zinc-400 text-xs"
                            >
                              {skill}
                            </Badge>
                          ))}
                          {job.skills_required?.length > 4 && (
                            <Badge
                              variant="outline"
                              className="bg-transparent border-zinc-700 text-zinc-500 text-xs"
                            >
                              +{job.skills_required.length - 4} more
                            </Badge>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Job Details */}
                  <div className="flex flex-wrap md:flex-col items-start gap-3 md:gap-2 md:text-right shrink-0">
                    {(job.budget_min || job.budget_max) && (
                      <div className="flex items-center gap-2 text-orange-500">
                        <Banknote className="h-4 w-4" />
                        <span className="font-medium">
                          {job.budget_min && job.budget_max
                            ? `R${job.budget_min.toLocaleString()} - R${job.budget_max.toLocaleString()}`
                            : job.budget_min
                            ? `From R${job.budget_min.toLocaleString()}`
                            : `Up to R${job.budget_max.toLocaleString()}`}
                        </span>
                      </div>
                    )}
                    
                    {job.location && (
                      <div className="flex items-center gap-2 text-zinc-400">
                        <MapPin className="h-4 w-4" />
                        <span>{job.location}</span>
                      </div>
                    )}
                    
                    {job.duration && (
                      <div className="flex items-center gap-2 text-zinc-400">
                        <Clock className="h-4 w-4" />
                        <span>{job.duration}</span>
                      </div>
                    )}

                    <div className="flex items-center gap-2 text-zinc-500">
                      <Users className="h-4 w-4" />
                      <span>{job.application_count || 0} applicants</span>
                    </div>

                    {job.is_featured && (
                      <Badge className="bg-orange-900/50 text-orange-400 border border-orange-700">
                        Featured
                      </Badge>
                    )}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        ) : (
          <div className="text-center py-16 bg-zinc-900 border border-zinc-800">
            <Briefcase className="h-12 w-12 text-zinc-700 mx-auto mb-4" />
            <h3 className="font-heading text-xl text-white mb-2">No jobs found</h3>
            <p className="text-zinc-500 mb-4">Try adjusting your filters or check back later</p>
            <Button
              onClick={() => navigate("/post-job")}
              className="bg-orange-600 hover:bg-orange-500"
            >
              Post the First Job
            </Button>
          </div>
        )}

        {/* Pagination */}
        {totalJobs > 20 && (
          <div className="flex justify-center gap-2 mt-12">
            <Button
              variant="outline"
              disabled={currentPage === 1}
              onClick={() => setCurrentPage(currentPage - 1)}
              className="border-zinc-700"
            >
              Previous
            </Button>
            <span className="flex items-center px-4 text-zinc-400">
              Page {currentPage} of {Math.ceil(totalJobs / 20)}
            </span>
            <Button
              variant="outline"
              disabled={currentPage >= Math.ceil(totalJobs / 20)}
              onClick={() => setCurrentPage(currentPage + 1)}
              className="border-zinc-700"
            >
              Next
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
